import type { CSSProperties } from 'react';

import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import { deriveContentToneFromOverlay } from '@/lib/derive-content-tone';
import { parseSectionOverlayColor, parseSectionOverlayOpacity } from '@/lib/parse-section-overlay';
import { readImageValue, readString, readVideoValue } from '@/lib/prop-utils';
import {
  parseSectionBackgroundSpacing,
  parseSectionBackgroundVerticalAlign,
  readSectionBackgroundType,
  type SectionBackgroundContentTone,
  type SectionBackgroundSpacing,
  type SectionBackgroundType,
  type SectionBackgroundVerticalAlign,
} from '@/lib/section-background';
import type { ImageValue } from '@/types/image';
import { getVideoUrl, type VideoValue } from '@/types/video';

export interface BackgroundConfig {
  mode: SectionBackgroundType;
  color?: string;
  image?: ImageValue;
  video?: VideoValue;
  overlayOpacity: number;
  overlayColor: string;
  spacing: SectionBackgroundSpacing;
  verticalAlign: SectionBackgroundVerticalAlign;
  contentTone: SectionBackgroundContentTone;
}

const resolveBackgroundMode = ({
  modeValue,
  hasVideo,
  hasImage,
  hasColor,
}: {
  modeValue: string | undefined;
  hasVideo: boolean;
  hasImage: boolean;
  hasColor: boolean;
}): SectionBackgroundType | null => {
  const explicitMode = readSectionBackgroundType(modeValue);

  if (explicitMode === 'image') return hasImage ? 'image' : hasColor ? 'color' : null;
  if (explicitMode === 'video') return hasVideo ? 'video' : hasColor ? 'color' : null;
  if (explicitMode === 'color') return 'color';

  if (hasVideo) return 'video';
  if (hasImage) return 'image';
  if (hasColor) return 'color';
  return null;
};

export const resolveBackgroundConfig = (
  props: Record<string, unknown>,
  siteTextColor?: string,
): BackgroundConfig | null => {
  const modeValue = readString(props.backgroundType);
  const image = readImageValue(props.backgroundImage);
  const video = readVideoValue(props.backgroundVideo);
  const color = readString(props.backgroundColor);

  const mode = resolveBackgroundMode({
    modeValue,
    hasVideo: Boolean(video),
    hasImage: Boolean(image),
    hasColor: Boolean(color),
  });

  if (!mode) return null;

  const overlayOpacity = parseSectionOverlayOpacity(props.backgroundOverlay);
  const overlayColor = parseSectionOverlayColor(props.backgroundOverlayColor);
  const contentTone: SectionBackgroundContentTone =
    mode === 'color'
      ? 'siteText'
      : deriveContentToneFromOverlay(overlayColor, overlayOpacity, siteTextColor);

  return {
    mode,
    color,
    image,
    video,
    overlayOpacity,
    overlayColor,
    spacing: parseSectionBackgroundSpacing(props.backgroundSpacing),
    verticalAlign: parseSectionBackgroundVerticalAlign(props.backgroundVerticalAlign),
    contentTone,
  };
};

export function BackgroundImage({
  image,
  focusX,
  focusY,
  fixed = false,
}: {
  image: ImageValue;
  focusX: number;
  focusY: number;
  fixed?: boolean;
}) {
  return (
    <ImageWithLoading
      image={image}
      alt=""
      aria-hidden
      className="h-full w-full object-cover"
      style={{
        objectPosition: `${focusX}% ${focusY}%`,
        ...(fixed ? { backgroundAttachment: 'fixed' as const } : null),
      }}
      fallback={
        <div className="h-full w-full bg-gradient-to-br from-foreground/80 to-foreground/90" />
      }
    />
  );
}

export function BackgroundMedia({
  config,
  focusX,
  focusY,
  fixed = false,
}: {
  config: BackgroundConfig;
  focusX: number;
  focusY: number;
  fixed?: boolean;
}) {
  if (config.mode === 'image' && config.image) {
    return <BackgroundImage image={config.image} focusX={focusX} focusY={focusY} fixed={fixed} />;
  }

  if (config.mode === 'video' && config.video) {
    const videoUrl = getVideoUrl(config.video);
    if (!videoUrl) {
      return (
        <div className="h-full w-full bg-gradient-to-br from-foreground/80 to-foreground/90" />
      );
    }

    return (
      <video
        className="h-full w-full object-cover"
        style={{ objectPosition: `${focusX}% ${focusY}%` }}
        src={videoUrl}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden
        tabIndex={-1}
      />
    );
  }

  const style: CSSProperties = {
    backgroundColor: config.color || 'hsl(var(--background))',
  };

  return <div className="h-full w-full" style={style} />;
}

export function BackgroundOverlay({ config }: { config: BackgroundConfig }) {
  const shouldRender =
    (config.mode === 'image' || config.mode === 'video') && config.overlayOpacity > 0;

  if (!shouldRender) return null;

  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundColor: config.overlayColor,
        opacity: config.overlayOpacity / 100,
      }}
    />
  );
}

export function AbsoluteBackgroundStack({
  config,
  focusX,
  focusY,
  fixed = false,
}: {
  config: BackgroundConfig;
  focusX: number;
  focusY: number;
  fixed?: boolean;
}) {
  return (
    <div className="absolute inset-0 z-0">
      <BackgroundMedia config={config} focusX={focusX} focusY={focusY} fixed={fixed} />
      <BackgroundOverlay config={config} />
    </div>
  );
}
